import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { IProjectResume } from "../../../../../application/states/project";
import { dataGridTexts } from "../../../../assets/helper-assets";
import { useAppSelector } from "../../../../../application/states/hooks";
import { useProjectService } from "../../../../../use-cases/project";
import { Box, Grid, Button } from "@mui/material";
import { format } from "date-fns";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditOffIcon from '@mui/icons-material/EditOff';
import { theme } from "../../../../styles";
import { NameCellRenderer } from "./projectDetails";

// FUNÇÃO DE PERMISSÕES
const canModifyProject = (project: IProjectResume, user: any, action: 'rename' | 'archive') => {
    // Tipos que só podem modificar seus próprios projetos
    const restrictedUserTypes = ['SELLER', 'OPERATOR', 'CUSTOMER EXPERIENCE', 'SALES SUPERVISOR'];
    
    // Se o usuário é de um tipo restritivo
    if (restrictedUserTypes.includes(user.userType)) {
        // Só pode modificar se criou o projeto
        return project.createdBy === user.id;
    }
    
    // Outros tipos podem modificar qualquer projeto
    return true;
};

export const ProjectListTable = (data: { projectList: IProjectResume[] }) => {

    const { page, total, active, pageSize } = useAppSelector((state) => state.project)
    const { isLoading } = useAppSelector((state) => state.loading)
    const { company } = useAppSelector((state) => state.company)
    const {
        setPage,
        openProject,
        openCreateProjectForm,
        setProjectName,
        updateProjectStatus,
        setPageSize
    } = useProjectService()
    const user = useAppSelector(state => state.user);

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'Nome',
            minWidth: 270,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box sx={{ overflow: 'visible' }}>

                <NameCellRenderer
                  project={params.value as IProjectResume}
                  onOpenProject={(id) => openProject({ projectId: id })}
                />
                    </Box>

              )
        },
        {
            field: 'createdBy',
            headerName: 'Criado por',
            minWidth: 250,
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Box
                    component="span"
                    title={params.value.userName}
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        textAlign: 'center'
                    }}
                >
                    {params.value.userName}
                </Box>
            )
        },
        {
            field: 'createdAt',
            headerName: 'Data de Criação',
            minWidth: 200,

            type: 'date',
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return format(new Date(params.value as string), 'dd/MM/yyyy - HH:mm ')
            }
        },
        {
            field: 'duplicate',
            headerAlign: 'center',
            align: 'center',
            headerName: 'Duplicar Projeto',
            minWidth: 150,
            renderCell: (params) => (
                <>
                    <Button onClick={() => {
                        openCreateProjectForm({
                            action: 'CLONE',
                            projectToCLone: params.value.id
                        })
                        setProjectName({
                            name: params.value.name + ' - Cópia'
                        })
                    }}>
                        <ContentCopyIcon />
                    </Button>
                </>
            )
        },
        {
            field: 'rename',
            headerAlign: 'center',
            align: 'center',
            headerName: 'Renomear Projeto',
            minWidth: 180,
            renderCell: (params) => {
                const project = params.value as IProjectResume;
                
                if (!canModifyProject(project, user, 'rename')) {
                    return (
                        <EditOffIcon color="disabled" titleAccess="Você não pode renomear este projeto" />
                    );
                }
                
                return (
                    <Button
                        onClick={() => {
                            openCreateProjectForm({
                                action: 'EDIT',
                                projectToCLone: project.id
                            });
                            setProjectName({
                                name: project.name
                            });
                        }}>
                        <EditIcon />
                    </Button>
                );
            }
        },
        {
            field: 'active',
            headerAlign: 'center',
            align: 'center',
            headerName: active ? 'Arquivar Projeto' : 'Desarquivar Projeto',
            minWidth: 180,
            renderCell: (params) => {
                const project = params.value as IProjectResume;
                
                if (!canModifyProject(project, user, 'archive')) {
                    return (
                        <Button disabled title="Você não pode arquivar este projeto">
                            <VisibilityOffIcon color="disabled" />
                        </Button>
                    );
                }
                
                return (
                    <Button
                        onClick={(e) => {
                            e.stopPropagation()
                            updateProjectStatus({
                                projectId: params.value.id,
                                status: !params.value.active
                            })
                        }
                    } >
                        {
                            active ?
                                <VisibilityOffIcon />
                                :
                                <VisibilityIcon />
                        }
                    </Button >
                )
            }
        },
    ]

    return (
        <Box
          sx={{
            // 1) permite que QUALQUER popup vaze para fora da grid
            overflow: 'visible',
    
            // 2) mantém o estilo de header
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme.palette.primary.main,
              color:
                company?.resale === true || company?.resaleId
                  ? theme.palette.primary.contrastText
                  : '#FFF',
            },
    
            // 3) garante que todas as camadas internas do DataGrid também sejam visíveis
            '& .MuiDataGrid-root': {
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-row': {
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'visible !important',
            },
            '& .MuiDataGrid-main': {
              overflow: 'visible !important',
            },
    
            minWidth: '100%',
          }}
        >
          <DataGrid
            autoHeight
            showCellVerticalBorder
            disableColumnMenu
            disableColumnFilter
            disableRowSelectionOnClick
            localeText={dataGridTexts}
            pagination
            pageSizeOptions={[10, 25, 50]}
            paginationModel={{ page: page - 1, pageSize }}
            onPaginationModelChange={m => {
              if (m.page + 1 !== page) {
                setPage({ page: m.page + 1 });
              }
              if (m.pageSize !== pageSize) {
                setPageSize({ pageSize: m.pageSize });
              }
            }}
            paginationMode="server"
            loading={isLoading}
            rowCount={total}
            rows={data.projectList.map(p => ({
              id: p.id,
              name: p,
              createdBy: p,
              createdAt: new Date(p.createdAt),
              duplicate: p,
              rename: p,
              active: p
            }))}
            columns={columns}
            slotProps={{
              pagination: { style: { marginRight: '35%' } }
            }}
            sx={{
              "& .MuiDataGrid-cell:focus-within": { outline: 0 },
              minWidth: '100%'
            }}
          />
        </Box>
      );
    };